"use client";
import { getReadableImageUrl } from '@/lib/directus'
import { sanitizeHTML } from '@/lib/utils'
import Image from "next/image";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useState } from "react";

interface FooterData {
	logo?: {
		image_file?: {
			image?: {
				id?: string
				filename_download?: string
			}
		}
		alt?: string
	}
	fax?: {
		title?: string
		short_description?: string
	}
	email?: {
		title?: string
		short_description?: string
	}
	phone?: {
		title?: string
		short_description?: string
	}

	social_links: {
		external_links_id: {
			title: string
			url: string
			label: string
			target: string
		}
	}[]
	footer_navigation: {
		menu_items: Array<{
			menu_items_id: {
				label: string
				href: string
				submenu: {
					label: string
					href: string
				}[]
			}
		}>
	}
	footer_address: {
		external_links_id: {
			title: string
			url: string
			label: string
			aria_label: string
			iframe: string
		}
	}[]
	legal_navigation: {
		menu_items: Array<{
			menu_items_id: {
				label: string
				href: string
			}
		}>
	}
	copyright?: {
		title?: string
	}
}

interface FooterProps {
  data: FooterData;
}

export const fields = [
	'logo.image_file.image.id',
	'logo.image_file.image.filename_download',
	'logo.alt',

	'fax.title',
	'fax.short_description',

	'email.title',
	'email.short_description',

	'phone.title',
	'phone.short_description',

	'social_links.external_links_id.title',
	'social_links.external_links_id.url',
	'social_links.external_links_id.target',

	'footer_navigation.menu_items.menu_items_id.label',
	'footer_navigation.menu_items.menu_items_id.href',
	'footer_navigation.menu_items.menu_items_id.submenu.label',
	'footer_navigation.menu_items.menu_items_id.submenu.href',

	'footer_address.external_links_id.title',
	'footer_address.external_links_id.url',
	'footer_address.external_links_id.aria_label',
	'footer_address.external_links_id.iframe',

	'legal_navigation.menu_items.menu_items_id.label',
	'legal_navigation.menu_items.menu_items_id.href',

	'copyright.title',
] as const

export default function Footer({ data }: FooterProps) {
	const [logoError, setLogoError] = useState(false);
	const pathname = usePathname();

	const handleLinkClick = () => {
		setLogoError(false);
	}

  return (
		<>
			<footer className='w-full bg-[#394448] overflow-hidden'>
				<div className='container py-12'>
					{/* Top section with logo and social icons */}
					<div className='w-full flex flex-wrap justify-center md:justify-between items-center gap-6 md:flex-row flex-col'>
						<div className='flex-1 flex flex-col justify-center items-center md:items-start gap-12 overflow-hidden'>
							<a className='siteLogoFooter' href='/' onClick={handleLinkClick}>
								<Image
									src={getReadableImageUrl(
										data?.logo?.image_file?.image?.id || '',
										data?.logo?.image_file?.image?.filename_download || '',
									)}
									alt={data?.logo?.alt || 'Harmon Facial Plastic Surgery'}
									width={216}
									height={69}
									className='w-[216px] h-[69px] relative'
									onError={() => setLogoError(true)}
								/>
							</a>
						</div>
						<div className='p-2.5 flex flex-col justify-center items-center md:items-start gap-2.5 overflow-hidden'>
							<div className='w-[178px] h-10 px-6 flex justify-between items-center'>
								{data?.social_links?.map((link, index) => (
									<Link
										key={index}
										href={link.external_links_id.url}
										target={link.external_links_id.target}
										rel={
											link.external_links_id.target === '_blank'
												? 'noopener noreferrer'
												: undefined
										}
										aria-label={
											link.external_links_id.target === '_blank'
												? `${link.external_links_id.label} (opens in new tab)`
												: link.external_links_id.label
										}
									>
										<Image
											src={`/images/f_${link.external_links_id.title.toLowerCase()}.svg`}
											alt={link.external_links_id.title}
											width={24}
											height={24}
											className='w-6 h-[23.31px]'
										/>
									</Link>
								))}
							</div>
						</div>
					</div>

					{/* Main footer content */}
					<div className='w-full pt-8 md:pt-16 flex flex-col md:flex-row justify-center md:justify-between items-center md:items-start gap-6'>
						{/* Contact Information */}
						<div className='w-full md:flex-1 flex flex-col justify-start items-center md:items-start gap-6'>
							<div className='w-full flex flex-col justify-start items-center md:items-start gap-2'>
								<p className='w-full text-center md:text-left text-white text-[20px] md:text-2xl font-bold font-brandon uppercase leading-loose tracking-wide'>
									{data?.phone?.short_description || 'Phone:'}
								</p>
								<p className='w-full text-center md:text-left text-white text-base font-normal font-brandon leading-normal'>
									<a
										href={`tel:${data?.phone?.title || '513-813-1400'}`}
										className='hover:underline'
									>
										{data?.phone?.title || '513-813-1400'}
									</a>
								</p>
							</div>
							<div className='w-full flex flex-col justify-start items-center md:items-start gap-2'>
								<p className='w-full text-center md:text-left text-white text-[20px] md:text-2xl font-bold font-brandon uppercase leading-loose tracking-wide'>
									{data?.fax?.short_description || 'Fax:'}
								</p>
								<p className='w-full text-center md:text-left text-white text-base font-normal font-brandon leading-normal'>
									{data?.fax?.title || '513-813-1415'}
								</p>
							</div>
							<div className='w-full flex flex-col justify-start items-center md:items-start gap-2'>
								<p className='w-full text-center md:text-left text-white text-[20px] md:text-2xl font-bold font-brandon uppercase leading-loose tracking-wide'>
									{data?.email?.short_description || 'Email:'}
								</p>
								<p className='w-full text-center md:text-left text-white text-base font-normal font-brandon leading-normal'>
									<a
										href={`mailto:${data?.email?.title || 'info@harmonface.com'}`}
										className='hover:underline'
									>
										{data?.email?.title || 'info@harmonface.com'}
									</a>
								</p>
							</div>
						</div>

						{/* Navigation Menu */}
						{data?.footer_navigation?.menu_items?.map((menuItem, index) => (
							<div
								key={index}
								className='w-full md:flex-1 flex flex-col justify-start items-center md:items-start gap-6'
							>
								<div className='w-full flex flex-col justify-start items-center md:items-start gap-2'>
									<p className='w-full text-center md:text-left text-white text-[20px] md:text-2xl font-bold font-brandon uppercase leading-loose tracking-wide'>
										<Link
											href={menuItem.menu_items_id.href}
											target={
												menuItem.menu_items_id.label === 'Patient Portal'
													? '_blank'
													: undefined
											}
											rel={
												menuItem.menu_items_id.label === 'Patient Portal'
													? 'noopener noreferrer'
													: undefined
											}
											className='hover:underline'
										>
											{menuItem.menu_items_id.label}
											{menuItem.menu_items_id.label === 'Patient Portal' && (
												<span className='sr-only'> (opens in new tab)</span>
											)}
										</Link>
									</p>
									{menuItem.menu_items_id.submenu?.map((subItem, subIndex) => (
										<Link
											key={subIndex}
											href={subItem.href}
											target={
												subItem.label === 'Patient Portal'
													? '_blank'
													: undefined
											}
											rel={
												subItem.label === 'Patient Portal'
													? 'noopener noreferrer'
													: undefined
											}
											className='w-full text-center md:text-left text-white text-base font-normal font-brandon leading-normal hover:underline'
										>
											{subItem.label}
											{subItem.label === 'Patient Portal' && (
												<span className='sr-only'> (opens in new tab)</span>
											)}
										</Link>
									))}
								</div>
							</div>
						))}
						{/* End of Navigation Menu */}
					</div>

					{/* Divider line */}
					<div className='w-full h-0 border-t-2 border-neutral-50/50 mt-8'></div>

					{/* Office locations section */}
					<div className='w-full pt-8 md:pt-12 flex flex-wrap justify-center items-stretch gap-12'>
						{data?.footer_address?.map((office, index) => (
							<div
								key={index}
								className='w-full min-w-[300px] flex flex-col justify-start items-center md:items-start gap-2.5'
							>
								<p className='text-center md:text-left text-white text-[20px] md:text-2xl font-bold font-brandon uppercase leading-loose tracking-wide'>
									{office.external_links_id.title}
								</p>
								<p
									className='text-center md:text-left text-white text-xl font-normal font-brandon leading-7 [&_a]:text-white [&_a]:underline-offset-4 [&_a:hover]:underline'
									dangerouslySetInnerHTML={sanitizeHTML(
										office.external_links_id.aria_label || '',
										{ stripLinkAriaLabels: true },
									)}
								/>
								<div className='w-full h-[284px] relative mt-2'>
									<iframe
										src={office.external_links_id.iframe || ''}
										width='600'
										height='450'
										style={{ border: 0 }}
										allowFullScreen
										loading='lazy'
										referrerPolicy='no-referrer-when-downgrade'
										className='w-full h-full'
									></iframe>
								</div>
							</div>
						))}
					</div>

					<div className='w-full h-0 border-t-2 border-neutral-50/50 mt-8'></div>

					{/* Bottom footer */}
					<div className='w-full flex flex-wrap justify-center md:justify-between items-center mt-12 gap-y-4'>
						<div className='flex flex-wrap justify-center md:justify-start items-center gap-6'>
							{data?.legal_navigation?.menu_items?.map((item, index) => (
								<Link
									key={index}
									href={item.menu_items_id.href}
									className='text-center md:text-left text-neutral-50 text-base font-normal font-brandon uppercase leading-normal tracking-widest hover:underline'
								>
									{item.menu_items_id.label}
								</Link>
							))}
						</div>
						<div className='text-center md:text-left text-neutral-50 text-base font-normal font-brandon uppercase leading-normal tracking-widest'>
							{data?.copyright?.title}
						</div>
						<div className='w-full text-center text-neutral-50 text-base font-normal font-brandon leading-normal tracking-widest'>
							All Rights Reserved. Web Design and Digital Marketing by{' '}
						<a
							href='https://webtoolsgroup.com'
							target='_blank'
							rel='noopener noreferrer'
							className='hover:underline'
						>
							Webtools Group
							<span className='sr-only'> (opens in new tab)</span>
						</a>
						. Digital Strategy by{' '}
						<a
							href='https://seoversite.com/'
							target='_blank'
							rel='noopener noreferrer'
							className='hover:underline'
						>
							SEOversite
							<span className='sr-only'> (opens in new tab)</span>
						</a>
						</div>
					</div>
				</div>
			</footer>
			{pathname !== '/contact' && (
				<div className='md:hidden fixed inset-x-0 bottom-0 z-40'>
					<a href='/contact' className='btn !rounded-none'>
						Book Your Consultation
					</a>
				</div>
			)}
		</>
	)
}